import React, { useEffect, useMemo, useState } from 'react';
import { formatInTenantTz } from '../../lib/datetime';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CreditCard, CheckCircle2, ArrowUpRight, Calendar, FileText, Crown, Zap, Package, ClipboardList, Wallet, AlertTriangle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { useTiers } from '../../hooks/useTiers';
import { getErrorMessage, normalizeError } from '../../lib/errorMessage';
import { useLanguage } from '../../contexts/LanguageContext';
import { getFreshIdToken } from '../../lib/firebase';
import { authedFetch } from '../../lib/authedFetch';
import { selectCurrentPlan, normalizeLimits } from '../../lib/planProjection';
import BillingCurrentPlanCard from './BillingCurrentPlanCard';
import BillingPlanChange from './BillingPlanChange';
import PurchaseReceiptsTable from './PurchaseReceiptsTable';
import BasePlanSubscriptionState from './BasePlanSubscriptionState';
import BasePlanInvoicesTable from './BasePlanInvoicesTable';
import EnterpriseReviewModal from './EnterpriseReviewModal';
import { submissionKey } from '../../lib/purchaseMode';
import { mfaApi, MFA_STATES, isValidOtp } from '../../lib/mfaClient';
import { tenantHeaders } from '../../lib/tenantSession';

const STATUS_BADGE = {
  trial: { label: 'Trial', className: 'bg-emerald-100 text-emerald-700' },
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  pending_payment: { label: 'Pending Payment', className: 'bg-amber-100 text-amber-700' },
  past_due: { label: 'Past Due', className: 'bg-red-100 text-red-700' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return formatInTenantTz(iso); } catch { return iso; }
};

const formatCurrency = (amount, currency = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount || 0);

// Factual one-line plan summary built from the canonical limits the server returns (NOT marketing copy). Ensures
// every plan card — including Starter, whose governed premium-feature list is empty — shows truthful content.
const planSummary = (tier) => {
  const L = tier?.limits || {};
  const parts = [];
  if (L.users != null && L.users !== '') parts.push(`${L.users} staff`);
  if (L.engagements != null && L.engagements !== '') parts.push(`${L.engagements} engagements/mo`);
  if (L.storage != null && L.storage !== '') parts.push(`${L.storage} storage`);
  return parts.join(' · ');
};

// Friendly labels for the server-derived assignment types (from /billing-state). No client inference.
const _ASSIGNMENT_LABEL = {
  paid_subscription: 'Paid subscription',
  grandfathered: 'Grandfathered plan',
  manual_or_custom_contract: 'Manual / custom contract',
  checkout_pending: 'Checkout pending',
  assigned_unpaid: 'Assigned — billing not active',
};

// GOV-13 commercial train: request lifecycle state → owner-facing badge.
const REQUEST_STATE_BADGE = {
  requested: { label: 'Requested', className: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under review', className: 'bg-indigo-100 text-indigo-700' },
  more_information_required: { label: 'More info required', className: 'bg-amber-100 text-amber-700' },
  approved_pending_billing: { label: 'Approved — payment required', className: 'bg-emerald-100 text-emerald-700' },
  declined: { label: 'Declined', className: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-200 text-gray-600' },
  expired: { label: 'Expired', className: 'bg-gray-200 text-gray-600' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-700' },
};

// Wallet health status → color badge (HEALTHY green / LOW amber / CRITICAL orange / DEPLETED red).
const HEALTH_BADGE = {
  HEALTHY: { label: 'Healthy', className: 'bg-green-100 text-green-700' },
  LOW: { label: 'Low', className: 'bg-amber-100 text-amber-700' },
  CRITICAL: { label: 'Critical', className: 'bg-orange-100 text-orange-700' },
  DEPLETED: { label: 'Depleted', className: 'bg-red-100 text-red-700' },
};

// Owner-safe backend error codes → friendly copy. Raw "Action failed." is never surfaced.
const COMMERCIAL_ERROR_TEXT = {
  REQUEST_NOT_APPROVED_FOR_BILLING: 'This request is not yet approved for billing. Payment becomes available once it is approved.',
  PRODUCT_NOT_ELIGIBLE: 'This product is not available on your current plan.',
  PRODUCT_VERSION_NOT_EFFECTIVE: 'This product version is not currently active. Please try again later.',
  PURCHASE_ROLE_REQUIRED: 'Only a Business Admin or Manager can perform this action.',
  PRODUCT_KEY_REQUIRED: 'Please select a product before requesting.',
  UNSUPPORTED_REQUEST_TYPE: 'That request type is not supported.',
  DUPLICATE_PENDING_REQUEST: 'You already have a pending request for this product.',
  ASSIGNMENT_NOT_FOUND: 'We could not find that assignment.',
  CONFLICTING_ACTIVE_ASSIGNMENT: 'This conflicts with an assignment you already have active.',
  COMMERCIAL_MONETIZATION_WRITE_DISABLED: 'Requests are temporarily unavailable. Please try again shortly.',
  COMMERCIAL_EMERGENCY_STOP_ACTIVE: 'Payments are temporarily paused by an operator. Please try again shortly.',
  REQUEST_APPROVAL_REQUIRED: 'This action requires approval first.',
  USAGE_CAPACITY_EXHAUSTED: 'This usage capacity is exhausted. Request an additional pack to continue.',
  USAGE_PACK_EXPIRED: 'This usage pack has expired.',
  REQUEST_ALREADY_PAID: 'This request has already been paid.',
  REQUEST_NOT_FOUND: 'We could not find that request.',
};

// Prefer the mapped friendly text for a known backend code; otherwise a safe normalized message.
const friendlyCommercialError = (err, fallback) => {
  const { message, code } = normalizeError(err, fallback);
  return (code && COMMERCIAL_ERROR_TEXT[code]) || message || fallback;
};

const Billing = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const { currentBusiness } = useBusiness();
  const { byId: tiersById, tiers: tierList } = useTiers();
  const [tenant, setTenant] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [entitlements, setEntitlements] = useState(null); // canonical plan+limits truth (/billing/me/entitlements)
  const [commercialInvoices, setCommercialInvoices] = useState([]); // GOV-13 paid add-on/pack purchase receipts
  const [refundByInvoice, setRefundByInvoice] = useState({});       // Domain A — refund status keyed by invoice_number
  const [requestingRefundKey, setRequestingRefundKey] = useState(null);

  const [commercialInvoicesForbidden, setCommercialInvoicesForbidden] = useState(false);
  const [downloadingCommercial, setDownloadingCommercial] = useState(null); // invoice_number currently downloading
  // GOV13 — Enterprise / on-request commercial-review request modal (REQUEST_REQUIRED plans; no Checkout, no plan change)
  const [reviewModal, setReviewModal] = useState({ open: false, env: null });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewResult, setReviewResult] = useState(null);
  const [invoices, setInvoices] = useState([]);          // DR-019 tenant-safe projection (no raw Stripe ids)
  const [billingState, setBillingState] = useState(null); // G2-E: authoritative server-derived billing state
  const [contactPending, setContactPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [upgradePending, setUpgradePending] = useState(null);
  const [planChangeOptions, setPlanChangeOptions] = useState([]);  // server BASE_PLAN purchase-mode envelopes
  const [planControlsEnabled, setPlanControlsEnabled] = useState(false);
  const [planChangeMeta, setPlanChangeMeta] = useState({});        // {current_plan_key, grandfathered}
  const [planBusyKey, setPlanBusyKey] = useState(null);
  const [downloadingRef, setDownloadingRef] = useState(null);
  const [stepUpFor, setStepUpFor] = useState(null);       // invoice awaiting a DR-017 step-up code
  const [stepUpCode, setStepUpCode] = useState('');
  const brandId = currentBusiness?.brandId;

  // Shared authenticated fetch (fresh Firebase ID token + single-flight refresh + one retry on token-expiry).
  // Billing NEVER reads the login-time localStorage token — that stale-cache path was the 401-storm regression.
  const refresh = async () => {
    if (!brandId) { setLoading(false); return; }
    setLoading(true);
    try {
      const t = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/by-brand/${brandId}`
      ).then(r => r.ok ? r.json() : null);
      if (!t) throw new Error('No tenant');
      setTenant(t);
      // DR-019: tenant-safe invoice history (billing-view role enforced server-side; no raw Stripe ids).
      // A 403 here means this role is not authorised for billing — handled as an empty, safe state.
      const inv = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/invoices/${t.id}`,
      ).then(r => r.ok ? r.json() : { invoices: [] }).catch(() => ({ invoices: [] }));
      setInvoices(inv.invoices || []);
      const tx = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/${t.id}/transactions`
      ).then(r => r.ok ? r.json() : { transactions: [] });
      setTransactions(tx.transactions || []);
      // G2-E: the ONE authoritative, server-derived billing-state projection (assigned vs actually-paid).
      const bs = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/${t.id}/billing-state`
      ).then(r => r.ok ? r.json() : null).catch(() => null);
      setBillingState(bs);
    } catch (e) {
      // Render empty state if unavailable
      setTenant(null);
      setTransactions([]);
      setBillingState(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [brandId]);

  // GOV-13 commercial reads (catalogue + my requests + wallet health). Server derives the tenant, so these
  // load independently of the brand-scoped billing fetch above. Every fetch is guarded — a 403/failure just
  // yields an empty, read-only surface rather than crashing the page.
  const commercialUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/me/commercial${path}`;

  // Domain A — Business refund REQUEST + STATUS (never approve/execute). Load current refund operations keyed by
  // invoice_number so each receipt row shows its truthful refund state; request creates a governed REQUESTED op.
  // authedFetch returns the raw Response — parse res.json() and gate on res.ok (canonical pattern).
  const loadRefunds = React.useCallback(async () => {
    try {
      const res = await authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/refunds`).catch(() => null);
      if (!res || !res.ok) return;
      const data = await res.json().catch(() => null);
      const list = (data && data.refunds) || [];
      const map = {};
      for (const r of list) { if (r.invoice_number && !map[r.invoice_number]) map[r.invoice_number] = r; }
      setRefundByInvoice(map);
    } catch (e) { /* non-fatal — receipts still render */ }
  }, []);
  useEffect(() => { loadRefunds(); }, [loadRefunds, commercialInvoices]);

  const onRequestRefund = async (inv) => {
    if (!inv?.invoice_number || requestingRefundKey) return;
    const reason = window.prompt(`Request a refund for ${inv.description || inv.product_key} (${inv.invoice_number})?\nEnter a reason (SuperAdmin will review and approve):`);
    if (reason == null || !reason.trim()) return;
    setRequestingRefundKey(inv.invoice_number);
    try {
      const res = await authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/refunds/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_number: inv.invoice_number, reason: reason.trim() }),
      });
      const data = res ? await res.json().catch(() => null) : null;
      if (res && res.ok && data?.refund_operation_id) {
        // truthful success — the canonical operation is created; the row will refresh to "Requested".
        toast({ title: 'Refund requested', description: 'Your request is now Requested — SuperAdmin will review it.' });
      } else if (res && res.status === 409) {
        // a refund request already exists for this receipt — NOT a failure; show the existing state, never "try again".
        toast({ title: 'Refund already requested', description: 'A refund request already exists for this receipt.' });
      } else {
        const code = data?.detail?.code;
        toast({ title: 'Could not request refund', description: code || 'Please try again.', variant: 'destructive' });
      }
      await loadRefunds();   // reflect the created/existing request as a status pill (a refresh failure never falsifies success)
    } catch (e) {
      // network/parse failure AFTER a possible successful POST → do not assert failure; just refresh the status.
      await loadRefunds();
    } finally { setRequestingRefundKey(null); }
  };

  // Canonical, server-derived plan truth. NEVER fall back to a tier-name guess (which could read
  // "Enterprise / Unlimited"): plan_display_name, version, grandfathered flag, and real limits are authoritative.
  const loadEntitlements = async () => {
    const data = await authedFetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/me/entitlements`,
    ).then(r => r.ok ? r.json() : null).catch(() => null);
    if (data) setEntitlements(data);
  };

  // GOV13 Step 5 — server BASE_PLAN change projection. Each option is resolve_purchase_mode(BASE_PLAN); the UI
  // renders strictly from it (no name/price/currentTier inference). A 403/failure yields an empty, read-only surface.
  const loadPlanChangeOptions = async () => {
    const res = await authedFetch(commercialUrl('/plan-change-options')).catch(() => null);
    if (!res || !res.ok) return;
    const d = await res.json().catch(() => null);
    if (d) {
      setPlanChangeOptions(d.plan_options || []);
      setPlanControlsEnabled(!!d.purchase_controls_enabled);
      setPlanChangeMeta({ current_plan_key: d.current_plan_key, grandfathered: !!d.grandfathered });
    }
  };

  // GOV-13 commercial purchase receipts (paid add-ons / packs). A 403 means this role may not view them —
  // rendered as a friendly, non-crashing state rather than an empty "nothing purchased" implication.
  const loadCommercialInvoices = async () => {
    const res = await authedFetch(commercialUrl('/invoices')).catch(() => null);
    if (!res) return;
    if (res.status === 403) { setCommercialInvoicesForbidden(true); setCommercialInvoices([]); return; }
    const data = await res.json().catch(() => null);
    if (data) { setCommercialInvoicesForbidden(false); setCommercialInvoices(data.invoices || []); }
  };

  const loadCommercial = async () => {
    // Step 4: Billing owns current plan + purchase receipts only. Add-on/pack catalogue, requests,
    // wallets and usage capacity live in Plan & Capabilities.
    await loadEntitlements();
    await loadCommercialInvoices();
    await loadPlanChangeOptions();
  };

  useEffect(() => { loadCommercial(); /* eslint-disable-next-line */ }, []);

  // DR-019 authenticated PDF download. Backend enforces role + tenant + paid status + DR-017 step-up.
  // On MFA_STEP_UP_REQUIRED we collect a 6-digit code, verify it, then retry the download EXACTLY once.
  const getToken = () => getFreshIdToken();
  async function fetchInvoicePdf(inv) {
    const token = await getToken();
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/invoices/${tenant.id}/${encodeURIComponent(inv.invoice_number)}/pdf`,
      { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } },
    );
    return res;
  }
  function streamBlob(blob, inv) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Servisfy-Invoice-${inv.invoice_number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }
  async function downloadInvoicePdf(inv) {
    if (downloadingRef) return; // double-submit guard
    setDownloadingRef(inv.invoice_number);
    try {
      const res = await fetchInvoicePdf(inv);
      if (res.status === 403) {
        const body = await res.json().catch(() => ({}));
        if (body?.detail?.state === MFA_STATES.STEP_UP_REQUIRED) { setStepUpFor(inv); return; }
        toast({ title: t('common.error'), description: t('pg.billing.notAuthorised'), variant: 'destructive' });
        return;
      }
      if (!res.ok) { toast({ title: t('common.error'), description: t('pg.billing.receiptUnavailable'), variant: 'destructive' }); return; }
      streamBlob(await res.blob(), inv);
    } catch {
      toast({ title: t('common.error'), description: t('pg.billing.receiptUnavailable'), variant: 'destructive' });
    } finally { setDownloadingRef(null); }
  }
  // GOV-13 commercial purchase-receipt PDF. Distinct endpoint from base-plan invoices
  // (/me/commercial/invoices/{n}/pdf); backend enforces role + tenant + document authority. The receipts table
  // renders this control ONLY when the backend marks a real document available, so this is never a no-op stub.
  async function downloadCommercialInvoice(inv) {
    if (downloadingCommercial) return; // double-submit guard
    setDownloadingCommercial(inv.invoice_number);
    try {
      const token = await getToken();
      const res = await fetch(
        commercialUrl(`/invoices/${encodeURIComponent(inv.invoice_number)}/pdf`),
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } },
      );
      if (res.status === 403) { toast({ title: t('common.error'), description: t('pg.billing.notAuthorised'), variant: 'destructive' }); return; }
      if (!res.ok) { toast({ title: t('common.error'), description: t('pg.billing.receiptUnavailable'), variant: 'destructive' }); return; }
      streamBlob(await res.blob(), inv);
    } catch {
      toast({ title: t('common.error'), description: t('pg.billing.receiptUnavailable'), variant: 'destructive' });
    } finally { setDownloadingCommercial(null); }
  }
  async function submitStepUpAndRetry() {
    if (!isValidOtp(stepUpCode)) { toast({ title: t('common.error'), description: t('pg.billing.enterCode'), variant: 'destructive' }); return; }
    const inv = stepUpFor;
    const v = await mfaApi.verify(stepUpCode.trim(), 'step_up', getToken);
    if (!v.ok) { toast({ title: t('common.error'), description: t('pg.billing.codeIncorrect'), variant: 'destructive' }); setStepUpCode(''); return; }
    setStepUpFor(null); setStepUpCode('');
    // retry the original download EXACTLY once, now that assurance exists
    setDownloadingRef(inv.invoice_number);
    try {
      const res = await fetchInvoicePdf(inv);
      if (res.ok) streamBlob(await res.blob(), inv);
      else toast({ title: t('common.error'), description: t('pg.billing.receiptUnavailable'), variant: 'destructive' });
    } finally { setDownloadingRef(null); }
  }

  // If we returned from Stripe, poll the session status to reflect upgrades immediately
  useEffect(() => {
    const session_id = params.get('session_id');
    if (!session_id) return;
    // A request payment also returns a session_id but is NOT a subscription upgrade — its own effect
    // (keyed on ?request=...) handles it.
    if (params.get('request')) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/checkout/status/${session_id}`
        );
        if (r.ok) {
          const data = await r.json();
          if (data.payment_status === 'paid') {
            toast({ title: 'Subscription updated', description: 'Your plan was upgraded successfully.' });
            await refresh();
            return;
          }
        }
      } catch (_e) { /* ignore */ }
      if (attempts < 6) setTimeout(tick, 2500);
    };
    tick();
    /* eslint-disable-next-line */
  }, [params]);

  const status = tenant?.subscription_status || (currentBusiness?.tier_id ? 'active' : 'pending_payment');
  const statusBadge = STATUS_BADGE[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
  const currentTierId = tenant?.tier_id || currentBusiness?.tier_id || 'professional';
  const currentTier = tiersById[currentTierId];

  // Canonical plan truth from entitlements. When present, the current-plan card renders THIS — not the
  // tier-name fallback that could falsely read "Enterprise / Unlimited".
  // Canonical current-plan + limits via the ONE shared projection adapter (identical to Plan & Capabilities).
  const plan = selectCurrentPlan(entitlements);
  const planLimits = normalizeLimits(entitlements);

  const trialEnd = tenant?.trial_end_at;
  const trialDaysLeft = useMemo(() => {
    if (!trialEnd || status !== 'trial') return null;
    const ms = new Date(trialEnd).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [trialEnd, status]);

  // GOV13 Step 5 — server-envelope-driven base-plan change. onChangePlan reuses the existing strict, webhook-
  // activated upgrade route; onRequestReview routes an exceptional (Enterprise/on-request) plan to contact-sales.
  // The plan is NEVER mutated on click — activation is the signed webhook + a later canonical refetch.
  const onChangePlan = async (env) => {
    if (planBusyKey) return;                         // one in-flight Checkout (double-click guard)
    setPlanBusyKey(submissionKey(env));
    try { await handleUpgrade(env.target_plan_key || env.product_key); }
    finally { setPlanBusyKey(null); }
  };
  // GOV13 — a REQUEST_REQUIRED (Enterprise / on-request) plan opens the commercial-review request modal. The plan is
  // NEVER assigned and NO Checkout is created; submitting persists a canonical tenant-scoped COMMERCIAL_REVIEW request
  // through the authenticated Business API (replaces the removed handleContactSales reference — root cause of the crash).
  const onRequestReviewPlan = (env) => { setReviewResult(null); setReviewModal({ open: true, env }); };
  const closeReviewModal = () => {
    if (reviewSubmitting) return;
    setReviewModal({ open: false, env: null }); setReviewResult(null);
  };
  const submitReview = async (form) => {
    setReviewSubmitting(true); setReviewResult(null);
    try {
      const res = await authedFetch(commercialUrl('/plan-review-request'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setReviewResult({ created: !!data.created, existing: !!data.existing, request: data.request, message: data.message });
      else {
        const d = data && data.detail;
        setReviewResult({ error: (d && (d.message || d.code)) || (typeof d === 'string' ? d : null) || 'Your request could not be submitted. Please try again.' });
      }
    } catch (e) {
      setReviewResult({ error: 'Network error — please try again.' });
    } finally { setReviewSubmitting(false); }
  };

  const handleUpgrade = async (targetTierId) => {
    // An established tenant is NEVER asked to "complete signup". If the billing profile has not finished loading
    // (or the brand-scoped fetch failed), this is a transient load state, not an unregistered tenant.
    if (!tenant?.id) {
      toast({ title: 'Billing details still loading', description: 'Your billing profile is still loading. Please retry in a moment.', variant: 'destructive' });
      return;
    }
    if (targetTierId === currentTierId && status === 'active') {
      toast({ title: 'Already on this plan', description: `You are already subscribed to ${currentTier?.name || targetTierId}.` });
      return;
    }
    setUpgradePending(targetTierId);
    try {
      const res = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/${tenant.id}/upgrade`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tier_id: targetTierId, origin_url: window.location.origin }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upgrade failed');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      toast({ title: 'Upgrade error', description: e.message, variant: 'destructive' });
    } finally {
      setUpgradePending(null);
    }
  };

  // Reflect the return from a request payment. The webhook is the fulfilment authority — this redirect only
  // surfaces a "submitted / awaiting fulfilment" or "cancelled" message and re-reads the authoritative
  // request list. Keyed on ?request=... so it never collides with a subscription-upgrade ?session_id return.
  useEffect(() => {
    if (!params.get('request')) return;
    if (params.get('session_id')) {
      toast({ title: 'Payment submitted', description: 'Payment submitted — awaiting fulfilment confirmation.' });
      // The webhook creates the receipt shortly after redirect — refetch a couple of times.
      loadCommercialInvoices();
      setTimeout(() => loadCommercialInvoices(), 3000);
      setTimeout(() => loadCommercialInvoices(), 8000);
    } else if (params.get('cancelled')) {
      toast({ title: 'Payment cancelled', description: 'Payment cancelled. No product or credits were applied.' });
    }
    /* eslint-disable-next-line */
  }, [params]);

  // Derive portal eligibility from already-loaded transactions — no extra fetch needed.
  // The backend /portal endpoint requires a Stripe customer (created at first checkout).
  // Trial tenants have no paid session yet, so we route them to checkout first.
  // Authoritative paid state comes from the server-derived billing-state projection; fall back to the
  // (also server-side) transaction records only if the projection is unavailable. Never inferred from a plan name.
  const hasPaidSession = billingState
    ? billingState.billing_provider_status === 'active_paid'
    : transactions.some(tx => tx.payment_status === 'paid');
  // A Stripe customer exists once ANY paid transaction has completed — including a paid add-on / credit-pack /
  // usage-pack purchase (commercial_creation="always"). The customer portal can then manage payment methods and
  // receipts even for a grandfathered tenant with NO active paid base-plan subscription. So portal availability
  // is broader than "has an active base subscription".
  const hasCommercialPurchases = commercialInvoices.length > 0;
  const canOpenPortal = hasPaidSession || hasCommercialPurchases || transactions.some(tx => tx.payment_status === 'paid');

  const [portalPending, setPortalPending] = useState(false);
  const handleOpenPortal = async () => {
    if (portalPending || !tenant) return;
    setPortalPending(true);
    try {
      if (!canOpenPortal) {
        // No Stripe customer yet (no paid base-plan OR commercial purchase) — run checkout for the current tier
        // to create one. After successful payment, Stripe returns to /business/billing?session_id=...
        // where refresh() picks up the paid transaction and the portal becomes available.
        if (!currentTierId) throw new Error('Unable to determine current subscription tier');
        if (process.env.NODE_ENV !== 'production') console.log('[Billing] Opening checkout flow', { tenantId: tenant.id, currentTierId, hasPaidSession });
        const res = await authedFetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/${tenant.id}/upgrade`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tier_id: currentTierId, origin_url: window.location.origin }),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Checkout failed');
        }
        const data = await res.json();
        window.location.href = data.url;
        return;
      }

      // Stripe customer exists — open customer portal directly.
      if (process.env.NODE_ENV !== 'production') console.log('[Billing] Opening Stripe portal', { tenantId: tenant.id });
      const res = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/${tenant.id}/portal`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ return_url: `${window.location.origin}/business/billing` }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Portal error');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      toast({ title: 'Billing unavailable', description: e.message, variant: 'destructive' });
    } finally {
      setPortalPending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="billing-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50" data-testid="billing-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">{t('pg.billing.title')}</h2>
            <p className="text-gray-600 text-sm">{t('pg.billing.subtitle')}</p>
          </div>
          <Badge className={statusBadge.className} data-testid="subscription-status-badge">
            {statusBadge.label}
          </Badge>
        </div>

        {/* Current plan summary — presentation extracted to BillingCurrentPlanCard (props = the shared
            selectCurrentPlan projection + normalizeLimits). No fetch/auth/catalogue inside the card. */}
        <BillingCurrentPlanCard plan={plan} planLimits={planLimits} loading={loading} status={status}
          trialDaysLeft={trialDaysLeft} billingState={billingState} t={t} />

        {/* GOV13 Step 5 — base-plan change is server-authoritative. Every target renders from the canonical
            resolve_purchase_mode(BASE_PLAN) envelope (upgrade/downgrade classification, price, downgrade blockers,
            Enterprise→Request review) — no plan-name / missing-price / currentTier / local-usage inference here. */}
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center"><ArrowUpRight className="h-5 w-5 mr-2 text-blue-500" />{t('pg.billing.changePlan')}</CardTitle>
            <CardDescription>{t('pg.billing.changePlanDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <BillingPlanChange options={planChangeOptions} canPurchase={planControlsEnabled}
              currentPlan={{ display_name: plan?.display_name, version: plan?.version,
                             grandfathered: planChangeMeta.grandfathered, current_plan_key: planChangeMeta.current_plan_key }}
              busyKey={planBusyKey} onChangePlan={onChangePlan} onRequestReview={onRequestReviewPlan} />
          </CardContent>
        </Card>

        {reviewModal.open && (
          <EnterpriseReviewModal env={reviewModal.env} defaultEmail={tenant?.admin_email || tenant?.email || ''}
            submitting={reviewSubmitting} result={reviewResult} onSubmit={submitReview} onClose={closeReviewModal} />
        )}

        {/* GOV13 Step 4 — add-on/pack catalogue, My Requests, wallets & credit health, and usage
            capacity now live in Plan & Capabilities (single owner). Billing keeps current plan,
            plan change, purchase receipts, base-plan payment methods and base-plan invoices. */}

        {/* GOV-13 §6 — COMMERCIAL PURCHASE RECEIPTS. Paid add-on / credit-pack / usage-pack invoices with
            GST breakdown and authenticated PDF receipts. Distinct from the subscription invoice history below. */}
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" data-testid="commercial-invoices-card">
          <CardHeader>
            <CardTitle className="flex items-center"><FileText className="h-5 w-5 mr-2 text-gray-600" />Purchase receipts</CardTitle>
            <CardDescription>Paid add-on, credit-pack, and usage-pack purchases. Download a tax receipt for each.</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Truthful, deduped, byte-precise receipt presentation (states + PDF-when-available) is extracted
                to a pure, testable component. Billing owns fetching/auth; the component only renders. */}
            <PurchaseReceiptsTable receipts={commercialInvoices} forbidden={commercialInvoicesForbidden}
              onDownload={downloadCommercialInvoice} downloadingKey={downloadingCommercial}
              onRequestRefund={onRequestRefund} refundByInvoice={refundByInvoice} requestingRefundKey={requestingRefundKey} t={t} />
          </CardContent>
        </Card>

        {/* Base-plan payment methods & subscription (Stripe customer portal). DISTINCT from Purchase receipts
            above — this manages the recurring BASE PLAN only. A grandfathered tenant has no base subscription
            but may still open the portal to manage payment methods once any paid purchase created a customer. */}
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center"><CreditCard className="h-5 w-5 mr-2 text-blue-500" />Base-plan payment methods &amp; subscription</CardTitle>
            <CardDescription>Manage your base-plan payment method, cancellation, and the Stripe customer portal. Add-on and pack receipts are shown above under Purchase receipts.</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Plan identity (BillingCurrentPlanCard, above) is separate from subscription/payment state here.
                A valid canonical plan is never shown inactive; there is NO grey "Activate base plan" button;
                the Stripe portal appears only when canonically eligible AND the caller may manage billing. */}
            <BasePlanSubscriptionState plan={plan} billingState={billingState} portalEligible={canOpenPortal}
              canManage={planControlsEnabled} onOpenPortal={handleOpenPortal} portalPending={portalPending} />
          </CardContent>
        </Card>

        {/* Base-plan subscription invoices — recurring BASE PLAN charges only. Commercial add-on/pack receipts
            are shown above under Purchase receipts (distinct authority). Empty is truthful for a grandfathered
            tenant and never implies "no payments at all". */}
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center"><FileText className="h-5 w-5 mr-2 text-gray-600" />Base-plan subscription invoices</CardTitle>
            <CardDescription>Invoices generated for an active paid base-plan subscription. Add-on and pack receipts appear above under Purchase receipts.</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Base-plan subscription invoices ONLY (recurring base plan). One-time purchase receipts never
                appear here; the empty state is truthful and never implies the plan is invalid. */}
            <BasePlanInvoicesTable invoices={invoices} hasCommercialReceipts={commercialInvoices.length > 0}
              onDownload={downloadInvoicePdf} downloadingKey={downloadingRef} t={t} />
            {stepUpFor && (
                <div className="mt-4 border border-blue-200 bg-blue-50 rounded-md p-3 max-w-sm">
                  <p className="text-sm text-blue-800 mb-2">{t('pg.billing.stepUpPrompt')}</p>
                  <div className="flex gap-2">
                    <input inputMode="numeric" maxLength={6} value={stepUpCode}
                      onChange={(e) => setStepUpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="flex-1 px-3 py-2 border rounded-md text-center tracking-widest font-mono" placeholder="123456" aria-label="verification code" />
                    <Button size="sm" onClick={submitStepUpAndRetry}>{t('common.verify') || 'Verify'}</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setStepUpFor(null); setStepUpCode(''); }}>{t('common.cancel')}</Button>
                  </div>
                </div>
              )}
          </CardContent>
        </Card>

        <div className="text-xs text-gray-400 flex items-center">
          <CreditCard className="h-3 w-3 mr-1" />{t('pg.billing.paymentsSecure')}
        </div>
      </div>
    </div>
  );
};

export default Billing;